import {
  amountInputToMicros,
  maskAmountInput,
  microsToAmountInput,
} from './GlobalConfiguration';
import { MICROS_PER_UNIT } from './Wallet';

/**
 * Contrato do BFF admin de precificação (CU-86akkmev0), descrito em
 * `docs/BFF-PRICING.md`: custos por serviço, markups, preço de contrato, janelas
 * de câmbio e preço resolvido.
 *
 * ## Append-only
 *
 * Não existe PUT nem DELETE — eles respondem 405. Todo POST insere uma VERSÃO
 * nova com `effective_from`, e é isso que mantém a cobrança de ontem explicável
 * depois da mudança de preço de hoje. Por isso o vocabulário daqui é "vigência",
 * nunca "edição", e a listagem é histórico, não cadastro.
 *
 * ## Markup: o mais específico VENCE
 *
 * `owner+serviço → owner → global+serviço → global → 1`. Um só vence; eles não se
 * somam nem se multiplicam. É a semântica do `User::aiMarkup()`, e tratar como
 * produto cobraria 1,95× onde se cobra 1,5×.
 *
 * ## Números
 *
 * `unit_cost`, `unit_price` e `factor` são **string decimal** no contrato, nunca
 * número JSON: `0.00015` como float já sai daqui com outro valor. `*_micros` é
 * **inteiro** (1 BRL = 1.000.000) e `id` é **string** (BIGINT serializado).
 */

// =============================================================================
// Serviços e unidades
// =============================================================================

export type PricingServiceName = 'whatsapp' | 'sms' | 'call' | 'ai' | 'rcs_basic' | 'rcs_single';

/** Como a simulação mede o uso daquele serviço. */
export type PricingUsageShape = 'quantity' | 'tokens';

export interface PricingUnitSpec {
  name: string;
  label: string;
}

export interface PricingServiceSpec {
  name: PricingServiceName;
  label: string;
  units: PricingUnitSpec[];
  /** `ai` é o único com variant (`provedor:modelo`), e nele ela é obrigatória. */
  requiresVariant: boolean;
  usage: PricingUsageShape;
}

/**
 * Tabela do contrato §4. A dupla serviço↔unidade é validada NA CARTEIRA — esta
 * lista monta o seletor, e quando a carteira recusar (400) é o `details.allowed`
 * dela que manda, não isto aqui.
 *
 * A ligação é cobrada por BLOCO DE 30 s, não por segundo: `block_30s` cru na
 * tela faria o operador cadastrar o preço do segundo.
 */
export const PRICING_SERVICES: PricingServiceSpec[] = [
  {
    name: 'whatsapp',
    label: 'WhatsApp',
    units: [{ name: 'message', label: 'Mensagem' }],
    requiresVariant: false,
    usage: 'quantity',
  },
  {
    name: 'sms',
    label: 'SMS',
    units: [{ name: 'message', label: 'Mensagem' }],
    requiresVariant: false,
    usage: 'quantity',
  },
  {
    name: 'call',
    label: 'Ligação',
    units: [{ name: 'block_30s', label: 'Bloco de 30 segundos' }],
    requiresVariant: false,
    usage: 'quantity',
  },
  // RCS (17tm466frtx): preço por mensagem pelo tipo EFETIVO (Basic = só texto; Single = card
  // com título, imagem ou botão). Sem preço cadastrado o envio RCS fica bloqueado (D7).
  {
    name: 'rcs_basic',
    label: 'RCS Basic',
    units: [{ name: 'message', label: 'Mensagem' }],
    requiresVariant: false,
    usage: 'quantity',
  },
  {
    name: 'rcs_single',
    label: 'RCS Single',
    units: [{ name: 'message', label: 'Mensagem' }],
    requiresVariant: false,
    usage: 'quantity',
  },
  {
    name: 'ai',
    label: 'IA',
    units: [
      { name: 'input_1k_tokens', label: 'Tokens de entrada (1k)' },
      { name: 'output_1k_tokens', label: 'Tokens de saída (1k)' },
    ],
    requiresVariant: true,
    usage: 'tokens',
  },
];

/**
 * Produtos que têm PREÇO DE LISTA por unidade (CU-86akn0zp6), na ordem em que a
 * tabela-resumo os mostra.
 *
 * A IA fica de fora de propósito: ela não tem preço de tabela — é
 * `tokens × câmbio × markup`, e listá-la ao lado de SMS e WhatsApp faria a tela
 * prometer um campo de preço que não existe para ela.
 */
export const PRICED_SERVICES: PricingServiceName[] = ['sms', 'whatsapp', 'call', 'rcs_basic', 'rcs_single'];

/** Moeda da carteira: o preço de contrato só existe nela (aperto do BFF). */
export const WALLET_CURRENCY = 'BRL';

/** Moedas de CUSTO oferecidas: a IA é cotada em USD, o resto em BRL. */
export const COST_CURRENCIES = ['BRL', 'USD'];

/** Par de câmbio padrão da carteira. */
export const DEFAULT_FX_PAIR = 'USD-BRL';

/** Teto de linhas por consulta nas listas do BFF (1..500). */
export const PRICING_LIMIT_MAX = 500;

/** Recorte que cabe na tela sem esconder que a lista pode ter sido cortada. */
export const PRICING_LIMIT_DEFAULT = 100;

export function pricingServiceSpec(service: string | null | undefined): PricingServiceSpec | null {
  return PRICING_SERVICES.find((item) => item.name === service) || null;
}

/** Rótulo do serviço; o que este painel não conhece sai como veio. */
export function pricingServiceLabel(service: string | null | undefined): string {
  if (!service) {
    return 'Todos os serviços';
  }

  return pricingServiceSpec(service)?.label || service;
}

const PRICING_SERVICE_ICONS: { [service: string]: string } = {
  sms: 'fa-commenting',
  whatsapp: 'fa-whatsapp',
  call: 'fa-phone',
  ai: 'fa-magic',
};

/**
 * Ícone do produto nas telas de Preços, já com a classe da cor (`service-icon--<produto>`,
 * em `admin-pricing/_pricing.scss`). Produto desconhecido fica com a etiqueta neutra.
 */
export function pricingServiceIcon(service: string | null | undefined): string {
  const icon = PRICING_SERVICE_ICONS[String(service)] || 'fa-tag';

  return `${icon} service-icon--${service || 'all'}`;
}

export function pricingUnitLabel(unit: string | null | undefined): string {
  if (!unit) {
    return '';
  }

  for (const service of PRICING_SERVICES) {
    const found = service.units.find((item) => item.name === unit);

    if (found) {
      return found.label;
    }
  }

  return unit;
}

export function pricingUnitsOf(service: string | null | undefined): PricingUnitSpec[] {
  return pricingServiceSpec(service)?.units || [];
}

// =============================================================================
// Linhas das listagens
// =============================================================================

/** Quem inseriu a versão de custo. */
export type ServiceCostSource = 'admin' | 'seed' | 'model_sync';

export interface ServiceCost {
  id: string;
  /** Só a resposta do POST traz `realm`; a listagem projeta colunas e não devolve. */
  realm?: string;
  service: string;
  variant: string | null;
  unit: string;
  cost_currency: string;
  /** String decimal — nunca converter para número antes de mandar de volta. */
  unit_cost: string;
  effective_from: string;
  created_at: string;
  source: ServiceCostSource | string;
}

/**
 * Degrau de preço de TABELA (CU-86akn0zp6). A ordem de precedência inteira é
 * `preço da conta (contrato) → preço de assinante → preço padrão do produto`;
 * estes dois são os degraus que esta tela cadastra.
 */
export type PricingTier = 'standard' | 'subscriber';

export interface PricingTierSpec {
  name: PricingTier;
  label: string;
}

/** Rótulos na língua da tela: o contrato fala `standard`, o operador fala "Padrão". */
export const PRICE_TIERS: PricingTierSpec[] = [
  { name: 'standard', label: 'Padrão' },
  { name: 'subscriber', label: 'Assinante' },
];

export function pricingTierLabel(tier: string | null | undefined): string {
  return PRICE_TIERS.find((item) => item.name === tier)?.label || String(tier || '');
}

/** Quem inseriu a versão de preço: a mão do admin ou a carga inicial. */
export type ServicePriceSource = 'admin' | 'seed';

/**
 * Preço de tabela de um produto num degrau (`GET/POST /admin/wallet/service-prices`).
 *
 * Mesmas regras das outras rotas de precificação: `unit_price` é **string
 * decimal** (nunca número JSON), `id` é string, e a rota é append-only — cada
 * POST é uma vigência nova.
 */
export interface ServicePrice {
  id: string;
  /** Só a resposta do POST traz `realm`; a listagem projeta colunas e não devolve. */
  realm?: string;
  service: string;
  variant: string | null;
  unit: string;
  tier: PricingTier;
  currency: string;
  unit_price: string;
  effective_from: string;
  created_at: string;
  source: ServicePriceSource | string;
}

/**
 * De onde saiu o preço que a carteira aplicou, na língua da tela.
 *
 * Os três primeiros são a escada de precificação em ordem de precedência. Mostrar
 * o código cru (`subscriber`) obrigaria quem lê a tela a saber o contrato do BFF
 * para entender por que o cliente pagou aquilo.
 */
export const PRICING_SOURCE_LABELS: { [source: string]: string } = {
  contract: 'Preço de contrato',
  subscriber: 'Preço de assinante',
  standard: 'Preço padrão',
  cost: 'Custo × markup × câmbio',
  raw: 'Valor pronto',
};

export function pricingSourceLabel(source: string | null | undefined): string {
  if (!source) {
    return 'sem origem';
  }

  return PRICING_SOURCE_LABELS[source] || source;
}

export interface Markup {
  id: string;
  realm?: string;
  /** Nulo = markup global. */
  owner_type: string | null;
  owner_id: string | null;
  /** Nulo = vale para todos os serviços (degrau mais genérico da resolução). */
  service: string | null;
  factor: string;
  effective_from: string;
  created_at: string;
}

export interface PriceOverride {
  id: string;
  realm?: string;
  owner_type: string | null;
  owner_id: string | null;
  service: string;
  unit: string;
  variant: string | null;
  currency: string;
  unit_price: string;
  effective_from: string;
  created_at: string;
}

export type FxWindowReason = 'daily_open' | 'variation_guard' | 'manual' | 'carry_over_stale';

export interface FxWindow {
  id: string;
  pair: string;
  /** Data em America/Sao_Paulo, `YYYY-MM-DD`, sem hora. */
  window_date: string;
  rate: string;
  fx_rate_id: string | null;
  reason: FxWindowReason | string;
  /** Instante UTC em que a janela abriu. */
  opened_at: string;
}

export const FX_REASON_LABELS: { [reason: string]: string } = {
  daily_open: 'Primeira taxa do dia',
  variation_guard: 'Desvio abriu janela nova',
  manual: 'Aberta na mão',
  carry_over_stale: 'Sem taxa no dia — carregou a anterior',
};

export function fxReasonLabel(reason: string | null | undefined): string {
  if (!reason) {
    return '';
  }

  return FX_REASON_LABELS[reason] || reason;
}

/** O dia não teve taxa aceita: a cobrança usou a taxa de antes. */
export function isFxStale(window: FxWindow): boolean {
  return window.reason === 'carry_over_stale';
}

/** Envelope das quatro listagens: `{ data: [...] }`, sem paginação. */
export interface PricingList<T> {
  data: T[];
}

// =============================================================================
// Simulação
// =============================================================================

export interface PricingBreakdown {
  /** `contract` = preço de contrato, `cost` = custo × markup × câmbio. */
  source: string;
  service: string;
  variant: string | null;
  usage: { [key: string]: number } | null;
  cost_currency: string | null;
  base_exact: string | null;
  /** Um dos dois vem preenchido e o OUTRO vem nulo: eles não se multiplicam. */
  markup_global: string | null;
  markup_owner: string | null;
  fx_pair: string | null;
  fx_rate: string | null;
  fx_window_id: string | null;
  unit_price_exact: string | null;
  amount_exact: string | null;
  amount_micros: number;
  service_cost_ids: string[];
  /** No máximo UM id: só um markup vence. */
  markup_ids: string[];
  price_override_ids: string[];
}

export interface PricingListFilters {
  service?: string;
  variant?: string;
  unit?: string;
  tier?: string;
  owner_type?: string;
  owner_id?: string;
  pair?: string;
  limit?: number;
}

// =============================================================================
// Vigências
// =============================================================================

/**
 * Versões de uma mesma chave (serviço+variant+unidade, ou escopo do markup),
 * separadas no que a tela precisa dizer: o que vale AGORA, o que ainda vai
 * valer, e tudo o que já valeu.
 *
 * A listagem do BFF não marca a vigente — "quem destaca é a tela" (contrato
 * §5.1). Sem esta separação o admin leria a primeira linha como a que está
 * valendo, e uma vigência agendada para amanhã apareceria como preço de hoje.
 */
export interface PricingVersionGroup<T> {
  key: string;
  /** Vigente: maior `effective_from` que já entrou em vigor. */
  current: T | null;
  /** Ainda não entraram em vigor, da mais próxima para a mais distante. */
  scheduled: T[];
  /** Todas as versões da chave, da mais nova para a mais velha. */
  versions: T[];
}

interface PricingVersion {
  id: string;
  effective_from: string;
  created_at?: string;
}

/**
 * O "agora" para decidir o que já está vigente: o mais tarde entre o relógio do navegador e o
 * `created_at` mais novo da resposta.
 *
 * A vigência gravada sem data vale "a partir de agora" pelo relógio da CARTEIRA. Em 30/09/2026
 * o servidor de HML estava ~4 s adiantado em relação ao Windows do operador: o preço recém
 * gravado parecia agendado para o futuro, e a tabela "Vigente agora" continuava mostrando o
 * antigo até um refresh. Uma linha que o servidor já criou prova que o relógio dele passou do
 * `created_at` dela. Vigência de fato agendada (effective_from > created_at) segue agendada.
 */
export function referenceNow(rows: PricingVersion[], now: Date = new Date()): Date {
  const newestCreated = (rows || []).reduce((newest, row) => {
    const time = new Date(String(row.created_at)).getTime();
    return isFinite(time) && time > newest ? time : newest;
  }, Number.NEGATIVE_INFINITY);

  return newestCreated > now.getTime() ? new Date(newestCreated) : now;
}

/**
 * Agrupa as versões por chave preservando a ordem em que as chaves aparecem na
 * resposta (a lista vem da versão mais nova para a mais velha, então a chave
 * mexida por último fica em cima).
 */
export function groupVersions<T extends PricingVersion>(
  rows: T[],
  keyOf: (row: T) => string,
  now: Date = referenceNow(rows)
): PricingVersionGroup<T>[] {
  const groups: PricingVersionGroup<T>[] = [];
  const index: { [key: string]: PricingVersionGroup<T> } = {};

  (rows || []).forEach((row) => {
    const key = keyOf(row);

    if (!index[key]) {
      index[key] = { key, current: null, scheduled: [], versions: [] };
      groups.push(index[key]);
    }

    index[key].versions.push(row);
  });

  const reference = now.getTime();

  groups.forEach((group) => {
    group.versions.sort(compareVersionsDesc);

    // A vigente é a primeira (mais nova) que já entrou em vigor; as anteriores a
    // ela na ordem são vigências futuras.
    group.current = group.versions.find((row) => effectiveTime(row) <= reference) || null;
    group.scheduled = group.versions
      .filter((row) => effectiveTime(row) > reference)
      .slice()
      .reverse();
  });

  return groups;
}

export function isScheduled(row: PricingVersion, now: Date = new Date()): boolean {
  return effectiveTime(row) > now.getTime();
}

/**
 * Mais nova primeiro. O desempate é pelo `id`, que é sequencial na carteira:
 * duas versões com o mesmo `effective_from` existem (dá para gravar duas vezes o
 * mesmo instante), e sem desempate a "vigente" oscilaria a cada render.
 */
function compareVersionsDesc(a: PricingVersion, b: PricingVersion): number {
  const difference = effectiveTime(b) - effectiveTime(a);

  return difference !== 0 ? difference : compareIdsDesc(a.id, b.id);
}

function compareIdsDesc(a: string, b: string): number {
  const first = Number(a);
  const second = Number(b);

  if (isFinite(first) && isFinite(second) && first !== second) {
    return second - first;
  }

  return String(b).localeCompare(String(a));
}

function effectiveTime(row: PricingVersion): number {
  const time = new Date(String(row.effective_from)).getTime();

  // Data ilegível não pode virar "vigente desde 1970": ela vai para o fim da
  // fila e a tela mostra o texto cru.
  return isFinite(time) ? time : Number.NEGATIVE_INFINITY;
}

// =============================================================================
// Resolução de markup
// =============================================================================

export type MarkupLevel = 'owner_service' | 'owner' | 'global_service' | 'global' | 'none';

export const MARKUP_LEVEL_LABELS: { [level: string]: string } = {
  owner_service: 'Cliente + serviço',
  owner: 'Cliente',
  global_service: 'Global + serviço',
  global: 'Global',
  none: 'Sem markup (1×)',
};

/** O degrau da escada em que aquela linha de markup mora. */
export function markupLevelOf(markup: Markup): MarkupLevel {
  if (markup.owner_id) {
    return markup.service ? 'owner_service' : 'owner';
  }

  return markup.service ? 'global_service' : 'global';
}

/** Do mais específico para o mais genérico — a ordem em que a carteira procura. */
export const MARKUP_LEVEL_ORDER: MarkupLevel[] = [
  'owner_service',
  'owner',
  'global_service',
  'global',
  'none',
];

export interface MarkupResolution {
  level: MarkupLevel;
  markup: Markup | null;
  /** O fator que vale. `null` no nível `none`, que equivale a 1×. */
  factor: string | null;
}

/**
 * O markup que a carteira aplicaria: `owner+serviço → owner → global+serviço →
 * global → 1`, o primeiro que existir.
 *
 * Um só vence. Esta função existe para a TELA poder dizer isso com números em
 * vez de só com texto — o cálculo de verdade é o do motor da carteira, com a
 * mesma janela de câmbio e a mesma resolução do débito.
 */
export function resolveMarkup(
  markups: Markup[],
  ownerId: string | null,
  service: string | null,
  now: Date = referenceNow(markups)
): MarkupResolution {
  const ladder: Array<{ level: MarkupLevel; matches: (markup: Markup) => boolean }> = [
    {
      level: 'owner_service',
      matches: (markup) => !!ownerId && markup.owner_id === ownerId && !!service && markup.service === service,
    },
    {
      level: 'owner',
      matches: (markup) => !!ownerId && markup.owner_id === ownerId && markup.service === null,
    },
    {
      level: 'global_service',
      matches: (markup) => markup.owner_id === null && !!service && markup.service === service,
    },
    {
      level: 'global',
      matches: (markup) => markup.owner_id === null && markup.service === null,
    },
  ];

  for (const step of ladder) {
    const current = currentVersion((markups || []).filter(step.matches), now);

    if (current) {
      return { level: step.level, markup: current, factor: current.factor };
    }
  }

  return { level: 'none', markup: null, factor: null };
}

/** A versão em vigor de um conjunto já filtrado por escopo. */
export function currentVersion<T extends PricingVersion>(rows: T[], now: Date = referenceNow(rows)): T | null {
  const reference = now.getTime();

  return (rows || [])
    .slice()
    .sort(compareVersionsDesc)
    .find((row) => effectiveTime(row) <= reference) || null;
}

// =============================================================================
// Números
// =============================================================================

/**
 * Casas decimais que o painel aceita em custo, preço e fator.
 *
 * O contrato aceita até 12, mas a carteira arredonda a cobrança em MICROS (6
 * casas) e é em micros que a tela mostra o dinheiro. Um custo com 12 casas seria
 * digitado, exibido arredondado e nunca conferido — então o campo recusa o que
 * não cabe na escala em vez de truncar em silêncio.
 */
export const PRICING_FRACTION_DIGITS = String(MICROS_PER_UNIT).length - 1;

/**
 * Micros inteiros → a string decimal que o contrato pede (`210000` → `"0.21"`).
 *
 * O recorte é feito na string do inteiro: dividir por 1.000.000 em float é o
 * jeito clássico de mandar `0.21000000000000002` para uma rota de faturamento.
 */
export function microsToDecimalString(micros: number | null | undefined): string | null {
  if (micros === null || micros === undefined || !isFinite(Number(micros))) {
    return null;
  }

  const amount = Math.trunc(Number(micros));

  if (amount < 0) {
    return null;
  }

  const digits = String(amount).padStart(PRICING_FRACTION_DIGITS + 1, '0');
  const whole = digits.slice(0, digits.length - PRICING_FRACTION_DIGITS);
  const fraction = digits.slice(digits.length - PRICING_FRACTION_DIGITS).replace(/0+$/, '');

  return fraction ? `${whole}.${fraction}` : whole;
}

/**
 * String decimal do contrato → micros inteiros, para o `moneyMicros` formatar.
 *
 * Devolve `null` quando o valor tem mais casas do que os micros comportam — é o
 * que faz a tela mostrar o número CRU em vez de um valor arredondado com cara de
 * exato.
 */
export function decimalStringToMicros(value: string | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  const raw = String(value).trim();

  if (!/^\d+(\.\d+)?$/.test(raw)) {
    return null;
  }

  const [whole, fraction = ''] = raw.split('.');

  if (fraction.length > PRICING_FRACTION_DIGITS) {
    return null;
  }

  const micros = Number(whole + fraction.padEnd(PRICING_FRACTION_DIGITS, '0'));

  return isFinite(micros) ? micros : null;
}

/**
 * O texto do campo (pt-BR, com vírgula) → a string decimal do contrato.
 *
 * Passa pelos micros de propósito: a máscara, o parser e a prévia são os MESMOS
 * da tela de configurações globais (`maskAmountInput` / `amountInputToMicros` /
 * `microsToAmountInput`), e dinheiro com duas implementações de conversão é
 * dinheiro com dois valores.
 */
export function amountInputToDecimalString(text: string | number | null | undefined): string | null {
  return microsToDecimalString(amountInputToMicros(text));
}

/** A string decimal do contrato → o texto editável do campo, em pt-BR. */
export function decimalStringToAmountInput(value: string | null | undefined): string {
  const micros = decimalStringToMicros(value);

  return micros === null ? '' : microsToAmountInput(micros);
}

/** Mantém no campo só o que compõe um decimal de 6 casas enquanto se digita. */
export function maskDecimalInput(text: string): string {
  return maskAmountInput(text);
}

/** Micros do que está digitado, para a prévia ao lado do campo. */
export function amountInputPreviewMicros(text: string | number | null | undefined): number | null {
  return amountInputToMicros(text);
}

/**
 * O fator como se lê: `"1.5"` → `"1,5×"`.
 *
 * Sem `Number`: o fator é string no contrato e sai da tela como string. Formatar
 * via float traria de volta o `1.3000000000000003` que a string evita.
 */
export function formatFactor(factor: string | null | undefined): string {
  if (factor === null || factor === undefined || String(factor).trim() === '') {
    return '';
  }

  return `${String(factor).trim().replace('.', ',')}×`;
}

/**
 * O produto que NÃO acontece, para a tela poder negá-lo com número.
 *
 * Mostrar "1,50 — não 1,95" é mais forte do que escrever "não multiplica": o
 * 1,95 é exatamente o valor que quem lê errado espera ver.
 */
export function multiplyFactors(a: string | null | undefined, b: string | null | undefined): string | null {
  const first = decimalStringToMicros(a);
  const second = decimalStringToMicros(b);

  if (first === null || second === null) {
    return null;
  }

  // Micros × micros dá 10⁻¹²; volta para micros dividindo pela escala, e o corte
  // é em inteiro porque o resultado só existe para ser mostrado como contraste.
  return microsToDecimalString(Math.round((first * second) / MICROS_PER_UNIT));
}

// =============================================================================
// Vigência
// =============================================================================

/** Recorte `YYYY-MM-DDTHH:mm` que o `datetime-local` aceita. */
const DATETIME_INPUT_LENGTH = 16;

/**
 * `YYYY-MM-DDTHH:mm` do campo → ISO-8601 com fuso EXPLÍCITO, que é o único
 * formato que o BFF aceita em `effective_from`.
 *
 * O fuso sai do navegador (`getTimezoneOffset`), não de uma constante: mandar
 * `-03:00` fixo erraria em três horas quem abrisse o painel de outro fuso, e o
 * erro apareceria só na fatura.
 */
export function datetimeInputToIso(value: string | null | undefined): string | null {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }

  const date = new Date(String(value));

  if (isNaN(date.getTime())) {
    return null;
  }

  return toIsoWithOffset(date);
}

/** O instante atual em ISO com fuso, para a vigência que vale "agora". */
export function nowIso(): string {
  return toIsoWithOffset(new Date());
}

/** O instante atual no formato do `datetime-local`, no fuso do navegador. */
export function nowDatetimeInput(): string {
  return isoToDatetimeInput(new Date().toISOString());
}

/** ISO do backend → `YYYY-MM-DDTHH:mm` no fuso do navegador. */
export function isoToDatetimeInput(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || String(iso).trim() === '') {
    return '';
  }

  const date = new Date(String(iso));

  if (isNaN(date.getTime())) {
    return '';
  }

  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

  return local.toISOString().slice(0, DATETIME_INPUT_LENGTH);
}

function toIsoWithOffset(date: Date): string {
  const offsetInMinutes = -date.getTimezoneOffset();
  const sign = offsetInMinutes >= 0 ? '+' : '-';
  const absolute = Math.abs(offsetInMinutes);

  return [
    date.getFullYear(),
    '-', pad(date.getMonth() + 1),
    '-', pad(date.getDate()),
    'T', pad(date.getHours()),
    ':', pad(date.getMinutes()),
    ':', pad(date.getSeconds()),
    sign, pad(Math.floor(absolute / 60)),
    ':', pad(absolute % 60),
  ].join('');
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

// =============================================================================
// Migração do balde para a carteira
// =============================================================================

/**
 * Migração do saldo antigo (baldes de unidade) para a carteira em R$
 * (CU-86akn0zp6): `GET /admin/wallet/migration/preview` e
 * `POST /admin/wallet/migration/run`.
 *
 * É cliente a cliente e MANUAL de propósito: converter balde em dinheiro usa o
 * preço vigente de cada produto, e um produto sem preço vigente não tem
 * conversão possível — a tela precisa mostrar o buraco antes de qualquer
 * gravação, não depois.
 *
 * IA não entra (decisão de 25/09/2026): ninguém usa IA em produção pelo balde, e
 * ela já nasce na carteira. O backend deixou de devolver a linha `ai`.
 */
export type MigrationTarget = 'sms' | 'whatsapp' | 'call';

/**
 * Ordem dos produtos na tela. Decisão de 29/09/2026: cada produto migra sozinho,
 * escolhido pelo administrador — nenhum vem marcado, porque a premissa é que o
 * saldo antigo (principalmente o de WhatsApp) expirou e a migração é a exceção
 * aprovada pelo comercial.
 */
export const MIGRATION_TARGETS: MigrationTarget[] = ['whatsapp', 'call', 'sms'];

/**
 * Produtos cuja migração liga a coorte (`wallet_billing_enabled`) no backend.
 * WhatsApp sozinho não liga: ele já sai da carteira para todo cliente.
 */
export const MIGRATION_COHORT_TARGETS: MigrationTarget[] = ['sms', 'call'];

/**
 * Situação de um produto no balde:
 *
 * - `ok` — tem unidades e preço; vira lançamento;
 * - `no_price` — tem unidades e NÃO há preço resolvido: não dá para converter;
 * - `zero` — não há unidades a converter;
 * - `migrated` — este produto já foi convertido antes;
 * - `pending` — fora da seleção da prévia, com unidades no pacote e sem migração;
 * - `not_allowed` — migração deste produto desligada em Configurações Globais
 *   (`WALLET_MIGRATION_SMS_CALL_ENABLED`, decisão de 29/09/2026);
 * - `route_only` — SMS ou ligação sem unidades e com preço: não há o que
 *   converter, mas migrar liga a cobrança do produto pela carteira (05/10/2026);
 * - `already_billed` — SMS ou ligação que a conta JÁ paga pela carteira, sem foto de
 *   migração (conta nascida depois do corte): não há o que migrar, e o balde dela não
 *   vira R$. O `run` devolve `skipped` (front-contrato-r3 §5);
 * - `reserved_in_flight` — há envio do produto no ar pelo pacote (broadcast de SMS
 *   saindo, ligação aguardando o PABX): o `run` recusa com 422 até ele terminar.
 *
 * Os dois últimos não se marcam e travam a seleção (ver `migrationBlocker`).
 */
export type MigrationProductStatus = 'ok' | 'no_price' | 'zero' | 'migrated' | 'pending' | 'not_allowed' | 'route_only'
  | 'already_billed' | 'reserved_in_flight';

/** Produto que não migra agora, por um motivo que não se conserta nesta tela. */
export const MIGRATION_UNAVAILABLE_STATUSES: ReadonlyArray<string> = ['already_billed', 'reserved_in_flight'];

/**
 * O que o `run` fez com cada produto; `replayed` é a idempotência funcionando e
 * `routed` é a cobrança ligada sem lançamento (balde vazio).
 */
export type MigrationEntryStatus = 'created' | 'replayed' | 'skipped' | 'routed';

export const MIGRATION_TARGET_LABELS: { [target: string]: string } = {
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  call: 'Ligação',
};

export const MIGRATION_STATUS_LABELS: { [status: string]: string } = {
  ok: 'Pronto para migrar',
  no_price: 'Sem preço — não dá para converter',
  zero: 'Sem saldo a converter',
  migrated: 'Já migrado',
  pending: 'Ainda no pacote',
  not_allowed: 'Migração desligada',
  route_only: 'Só liga a cobrança pela carteira',
  already_billed: 'Já cobrado pela carteira — nada a migrar',
  reserved_in_flight: 'Envio em andamento — migre quando terminar',
};

export const MIGRATION_ENTRY_STATUS_LABELS: { [status: string]: string } = {
  created: 'Lançamento criado',
  replayed: 'Já existia (idempotência)',
  skipped: 'Nada a lançar',
  routed: 'Cobrança ligada, sem lançamento',
};

export interface WalletMigrationUser {
  id: string;
  email: string;
  name: string;
  /** `true` = a carteira já é a cobrança desta conta. */
  wallet_billing_enabled: boolean;
  pricing_tier: string;
}

export interface WalletMigrationProduct {
  target: MigrationTarget | string;
  units: number;
  /** `null` quando não há preço resolvido — e aí `status` é `no_price`. */
  unit_price_micros: number | null;
  source: string | null;
  amount_micros: number;
  status: MigrationProductStatus | string;
  /** Está na seleção que a prévia calculou (29/09/2026). */
  selected?: boolean;
  /** ISO 8601 da migração deste produto, quando `status` é `migrated`. */
  migrated_at?: string | null;
  /** ISO 8601 do estorno (`wallet:rollback-migration`), se houve. */
  reversed_at?: string | null;
}

export interface WalletMigrationEntry {
  target: MigrationTarget | string;
  idempotency_key: string;
  status: MigrationEntryStatus | string;
}

export interface WalletMigrationPreview {
  user: WalletMigrationUser;
  /** A SELEÇÃO tem produto já migrado — é o 409 do `run` (por produto desde 29/09). */
  already_migrated: boolean;
  selected_products?: string[];
  /** Produtos da conta que já têm migração gravada, selecionados ou não. */
  migrated_products?: string[];
  already_migrated_products?: string[];
  /**
   * Produtos que podem migrar agora (29/09/2026). SMS e ligação só entram com
   * `WALLET_MIGRATION_SMS_CALL_ENABLED` ligada. A migração zera o pacote do produto
   * migrado e, em SMS e ligação, passa a cobrança dele para a carteira (T.9, 07/10).
   * Ausente = resposta anterior à regra, e aí tudo é permitido.
   */
  allowed_products?: string[];
  products: WalletMigrationProduct[];
  total_micros: number;
  warnings: string[];
}

/** A resposta do `run` é a prévia MAIS o que foi gravado. */
export interface WalletMigrationResult extends WalletMigrationPreview {
  entries: WalletMigrationEntry[];
}

/**
 * Por que a migração não pode rodar, ou `null` quando pode.
 *
 * Fica aqui, e não no template, porque é a MESMA regra que desabilita o botão e
 * que explica o porquê logo abaixo dele — duas cópias divergiriam, e a divergência
 * apareceria como um botão apagado sem motivo escrito.
 *
 * O caso que importa é o `no_price`: converter um balde sem preço vigente só
 * seria possível inventando um valor, e ligar a cobrança de SMS ou ligação sem
 * preço tiraria o cliente do ar. O conserto (cadastrar o preço) é a uma tela de
 * distância.
 */
export function migrationBlocker(
  preview: WalletMigrationPreview | null,
  selected?: ReadonlyArray<string>,
): string | null {
  if (!preview) {
    return 'Escolha um cliente para ver o que há para migrar.';
  }

  // Sem seleção informada vale a regra antiga (todos os produtos da prévia).
  const products = migrationSelectedProducts(preview, selected);

  if (selected !== undefined && products.length === 0) {
    return 'Escolha ao menos um produto para migrar.';
  }

  const migrated = products.filter((product) => product.status === 'migrated');

  if (selected === undefined ? preview.already_migrated : migrated.length > 0) {
    const names = migrationNames(migrated);

    return names
      ? `${names} já foi migrado. Tire da seleção: migrar de novo não criaria lançamento novo.`
      : 'Este cliente já foi migrado. Rodar de novo não criaria lançamento novo.';
  }

  const billed = products.filter((product) => product.status === 'already_billed');

  if (billed.length > 0) {
    return `${migrationNames(billed)} já é cobrado pela carteira: não há o que migrar. Tire da seleção.`;
  }

  const inFlight = products.filter((product) => product.status === 'reserved_in_flight');

  if (inFlight.length > 0) {
    return `${migrationNames(inFlight)}: há envio em andamento pelo pacote. Migre quando ele terminar — nada seria migrado agora.`;
  }

  const missing = products.filter((product) => product.status === 'no_price');

  if (missing.length > 0) {
    return `Cadastre o preço antes: ${migrationNames(missing)} não tem preço vigente.`;
  }

  const total = selected === undefined ? preview.total_micros : migrationSelectionTotal(preview, selected);
  // Balde vazio de SMS ou ligação não lança nada, mas migrar liga a cobrança.
  const routes = products.some((product) => product.status === 'route_only');

  if (!total && !routes) {
    return 'Não há saldo a converter: a migração não geraria nenhum lançamento.';
  }

  return null;
}

function migrationNames(products: WalletMigrationProduct[]): string {
  return products.map((product) => MIGRATION_TARGET_LABELS[product.target] || product.target).join(', ');
}

/** O produto pode ser migrado agora? Sem `allowed_products` na prévia, sim. */
export function migrationIsAllowed(preview: WalletMigrationPreview | null, target: string): boolean {
  const allowed = preview?.allowed_products;

  return !allowed || allowed.includes(target);
}

/** Os produtos da prévia que estão na seleção (todos, sem seleção informada). */
export function migrationSelectedProducts(
  preview: WalletMigrationPreview | null,
  selected?: ReadonlyArray<string>,
): WalletMigrationProduct[] {
  const products = preview?.products || [];

  return selected === undefined ? products : products.filter((product) => selected.includes(String(product.target)));
}

/**
 * Total em R$ da seleção. Soma os MESMOS `amount_micros` que o backend calculou
 * na prévia (quantidade cheia, arredondada uma vez na carteira) — nenhuma conta
 * nova aqui, só a escolha de quais linhas entram.
 */
export function migrationSelectionTotal(preview: WalletMigrationPreview | null, selected: ReadonlyArray<string>): number {
  return migrationSelectedProducts(preview, selected)
    .reduce((sum, product) => sum + (Number(product.amount_micros) || 0), 0);
}
